// The six shield bar styles of a Marks theme (ui-map-interface.md §6.1,
// themes.md §3.5, R425, R829).
//
// Every own hull carries a readout over it and every contact carries one
// beside it. `stack` is the shipped drawing and the AUTO value; the five
// below it are cards 02 to 06 of mockup round twenty-one
// (`archive/mockups/021-fleet-bars.html`). A style REPLACES the AUTO
// drawing; it never stacks on it.
//
// This is a leaf (`leaves.test.ts`): it holds the shape of each style and
// imports no engine. Every figure comes in as `f`, the generated
// `FLEET_BARS` of `presentation.fleets.bars`, so the file states no
// length, no offset and no alpha of its own. The sink is the narrow
// `RectSink` below, which `fast/layers/fleets.ts` fills from one reusable
// adapter over its segment batch: a butt-capped line of thickness `h` IS
// the rectangle, and a dashed lane is ONE instance because the segment
// shader evaluates the dash.
//
// The place of a contact's readout and the cargo lane's offset are
// figures too, so they are read from `f` through the two accessors of
// `BarStyle` rather than repeated here.

import type { FleetBars } from '../gen/presentation';
import type { ShieldStyle } from '../ui/settings';
import { clamp01 } from './math';

/** Where a contact's readout sits against its hull. */
export type ContactPlace = 'above' | 'below';

/**
 * What a style draws into.
 *
 * `left` and `top` are map px against the hull's own origin, so a style
 * measures from the hull and never from the camera. `dashOn` over zero
 * dashes the rectangle along its long side; zero is solid.
 */
export interface RectSink {
  rect(
    left: number, top: number, w: number, h: number,
    color: number, alpha: number, dashOn?: number, dashOff?: number,
  ): void;
}

/** What one hull's readout says. */
export interface BarValues {
  /** The hull figure, 0 to 100. */
  hullPct: number;
  /** The shield figure, 0 to 100. */
  shieldPct: number;
  /** The traffic-light band of an own hull bar. */
  hullColor: number;
  /** The flat faction colour a contact's hull lane wears. */
  faction: number;
}

/** One value of the `shield` field. */
export interface BarStyle {
  /** Where this style puts a contact's readout. */
  contactPlace(f: FleetBars): ContactPlace;
  /** Where this style puts the cargo lane, in map px against the hull. */
  cargoY(f: FleetBars): number;
  /** The readout of an own hull. */
  own(s: RectSink, v: BarValues, f: FleetBars): void;
  /** The readout of a contact. */
  contact(s: RectSink, v: BarValues, f: FleetBars): void;
}

/** The share `pct` takes of `w`, held to 0..100: a lane's width, or an alpha span. */
function span(w: number, pct: number): number {
  return w * Math.max(0, Math.min(100, pct)) / 100;
}

/** The dark ground a lane sits on. */
function ground(
  s: RectSink, f: FleetBars, left: number, top: number,
  w: number, h: number, alpha?: number,
): void {
  s.rect(left, top, w, h, f.ground, alpha ?? f.groundAlpha);
}

/**
 * A frame drawn as four rectangles, so it stays crisp at one px.
 *
 * The two uprights are inset by the thickness, the way the mockup page
 * draws a frame. `stack` keeps its own fuller frame below.
 */
function frame(
  s: RectSink, left: number, top: number, w: number, h: number,
  t: number, color: number, alpha: number,
): void {
  s.rect(left, top, w, t, color, alpha);
  s.rect(left, top + h - t, w, t, color, alpha);
  s.rect(left, top + t, t, h - 2 * t, color, alpha);
  s.rect(left + w - t, top + t, t, h - 2 * t, color, alpha);
}

/** The shipped hull bar: a ground under the traffic light. */
function hullBar(s: RectSink, v: BarValues, f: FleetBars, top: number): void {
  ground(s, f, f.x, top, f.w, f.h);
  s.rect(f.x, top, span(f.w, v.hullPct), f.h, v.hullColor, f.hullAlpha);
}

/** A contact's hull bar: a ground under the FLAT faction colour. */
function flatBar(s: RectSink, v: BarValues, f: FleetBars, top: number): void {
  ground(s, f, f.x, top, f.w, f.h);
  s.rect(f.x, top, span(f.w, v.hullPct), f.h, v.faction, f.contactHullAlpha);
}

/**
 * Card 01 "Stack" — the shipped readout (R425).
 *
 * A second bar stands one px over the hull bar, and a contact wears the
 * pair UNDER its hull. The contact's shield lane is hollow: a faint fill
 * inside an open frame, over a ground of its own, so an empty lane still
 * reads as a lane. That ground and the full-height uprights are what R425
 * shipped over the card, and the AUTO look is the shipped look.
 */
const stack: BarStyle = {
  contactPlace: (f) => f.stack.contactPlace,
  cargoY: (f) => f.stack.cargoY,
  own(s, v, f) {
    ground(s, f, f.x, f.ownShieldY, f.w, f.h);
    ground(s, f, f.x, f.ownHullY, f.w, f.h);
    s.rect(f.x, f.ownHullY, span(f.w, v.hullPct), f.h, v.hullColor, f.hullAlpha);
    s.rect(f.x, f.ownShieldY, span(f.w, v.shieldPct), f.h, f.shieldColor, f.ownShieldAlpha);
  },
  contact(s, v, f) {
    ground(s, f, f.x, f.contactHullY, f.w, f.h);
    ground(s, f, f.x, f.contactShieldY, f.w, f.h);
    s.rect(f.x, f.contactHullY, span(f.w, v.hullPct), f.h, v.faction, f.contactHullAlpha);
    s.rect(f.x, f.contactShieldY, span(f.w, v.shieldPct), f.h,
      f.shieldColor, f.contactShieldAlpha);
    const t = f.contactShieldY, k = f.contactFrameW;
    s.rect(f.x, t, f.w, k, f.shieldColor, f.contactFrameAlpha);
    s.rect(f.x, t + f.h - k, f.w, k, f.shieldColor, f.contactFrameAlpha);
    s.rect(f.x, t, k, f.h, f.shieldColor, f.contactFrameAlpha);
    s.rect(f.x + f.w - k, t, k, f.h, f.shieldColor, f.contactFrameAlpha);
  },
};

/**
 * Card 02 "Cap" — the shield caps the hull bar with no gap, so the two
 * lanes read as one five-px instrument. A contact keeps the place and the
 * shape, its hull lane flat and its cap dashed.
 */
const cap: BarStyle = {
  contactPlace: (f) => f.cap.contactPlace,
  cargoY: (f) => f.cap.cargoY,
  own(s, v, f) {
    const c = f.cap;
    ground(s, f, f.x, f.ownHullY - c.capH, f.w, c.groundH);
    s.rect(f.x, f.ownHullY - c.capH, span(f.w, v.shieldPct), c.capH, f.shieldColor, c.capAlpha);
    s.rect(f.x, f.ownHullY, span(f.w, v.hullPct), f.h, v.hullColor, f.hullAlpha);
  },
  contact(s, v, f) {
    const c = f.cap;
    const top = f.ownHullY;
    ground(s, f, f.x, top - c.capH, f.w, c.groundH);
    s.rect(f.x, top - c.capH, span(f.w, v.shieldPct), c.capH,
      f.shieldColor, c.contactCapAlpha, c.contactDashOn, c.contactDashOff);
    s.rect(f.x, top, span(f.w, v.hullPct), f.h, v.faction, f.contactHullAlpha);
  },
};

/**
 * Card 03 "Sheath" — the shield is a bracket over the hull bar: a rail
 * along the top and a cheek down each end, drawn from the left as far as
 * the shield holds. A full shield sheathes the bar and closes the right
 * cheek; a spent one leaves the bar bare. A contact has a flat hull bar
 * and a dotted rail in its OWN faction ink.
 */
const sheath: BarStyle = {
  contactPlace: (f) => f.sheath.contactPlace,
  cargoY: (f) => f.sheath.cargoY,
  own(s, v, f) {
    const c = f.sheath;
    hullBar(s, v, f, f.ownHullY);
    const reach = span(f.w + 2 * c.overhang, v.shieldPct);
    if (reach <= 0) return;
    s.rect(f.x - c.overhang, f.ownHullY + c.railDy, reach, c.railH, f.shieldColor, c.alpha);
    s.rect(f.x - c.overhang, f.ownHullY + c.cheekDy, c.cheekW, c.cheekH, f.shieldColor, c.alpha);
    if (v.shieldPct >= 100) {
      s.rect(f.x + f.w, f.ownHullY + c.cheekDy, c.cheekW, c.cheekH, f.shieldColor, c.alpha);
    }
  },
  contact(s, v, f) {
    const c = f.sheath;
    const top = f.ownHullY;
    flatBar(s, v, f, top);
    const reach = span(f.w + 2 * c.overhang, v.shieldPct);
    s.rect(f.x - c.overhang, top + c.railDy, reach, c.railH,
      v.faction, c.contactAlpha, c.contactDashOn, c.contactDashOff);
  },
};

/** The width of one tick segment, with the gaps taken out of the bar. */
function tickWidth(f: FleetBars): number {
  const c = f.ticks;
  return (f.w - (c.segments - 1) * c.gap) / c.segments;
}

/** How full segment `i` of `n` is, at `pct`. */
function tickFill(pct: number, i: number, n: number): number {
  return clamp01((pct - i * (100 / n)) / (100 / n));
}

/** One lane of tick segments at `top`, each as full as `pct` makes it. */
function tickLane(
  s: RectSink, f: FleetBars, top: number, pct: number, color: number, alpha: number,
): void {
  const c = f.ticks;
  const seg = tickWidth(f);
  ground(s, f, f.x, top, f.w, f.h);
  for (let i = 0; i < c.segments; i++) {
    const fill = tickFill(pct, i, c.segments);
    if (fill <= 0) continue;
    s.rect(f.x + i * (seg + c.gap), top, seg * fill, f.h, color, alpha);
  }
}

/**
 * Card 04 "Ticks" — the shield is four segments, quarter by quarter, so a
 * glance COUNTS what is left instead of measuring it. A contact wears both
 * lanes segmented under the hull: the hull segments flat, and four open
 * frames over them. An empty frame stays visible at `frame_off_alpha`, so
 * the reader counts the same four places at every level.
 */
const ticks: BarStyle = {
  contactPlace: (f) => f.ticks.contactPlace,
  cargoY: (f) => f.ticks.cargoY,
  own(s, v, f) {
    tickLane(s, f, f.ownShieldY, v.shieldPct, f.shieldColor, f.ticks.ownAlpha);
    hullBar(s, v, f, f.ownHullY);
  },
  contact(s, v, f) {
    const c = f.ticks;
    const seg = tickWidth(f);
    tickLane(s, f, f.contactHullY, v.hullPct, v.faction, c.contactHullAlpha);
    for (let i = 0; i < c.segments; i++) {
      const on = v.shieldPct > i * (100 / c.segments);
      frame(s, f.x + i * (seg + c.gap), f.contactShieldY, seg, f.h, c.frameW,
        f.shieldColor, on ? c.frameOnAlpha : c.frameOffAlpha);
    }
  },
};

/**
 * Card 05 "Lanes" — one ground plate carries both lanes, the shield on top
 * and the hull below, and the plate leaves a dark rule between them. A
 * contact wears the same plate under its hull, narrower, its hull lane
 * flat and its shield lane at half ink.
 */
const lanes: BarStyle = {
  contactPlace: (f) => f.lanes.contactPlace,
  cargoY: (f) => f.lanes.cargoY,
  own(s, v, f) {
    const c = f.lanes;
    ground(s, f, f.x, f.ownHullY + c.plateDy, f.w, c.plateH, c.plateAlpha);
    s.rect(f.x, f.ownHullY + c.plateDy, span(f.w, v.shieldPct), c.shieldH,
      f.shieldColor, f.ownShieldAlpha);
    s.rect(f.x, f.ownHullY, span(f.w, v.hullPct), f.h, v.hullColor, f.hullAlpha);
  },
  contact(s, v, f) {
    const c = f.lanes;
    const left = f.x + (f.w - c.contactW) / 2;
    ground(s, f, left, f.contactHullY + c.plateDy, c.contactW, c.plateH, c.plateAlpha);
    s.rect(left, f.contactHullY + c.plateDy, span(c.contactW, v.shieldPct), c.shieldH,
      f.shieldColor, c.contactShieldAlpha);
    s.rect(left, f.contactHullY, span(c.contactW, v.hullPct), f.h,
      v.faction, f.contactHullAlpha);
  },
};

/**
 * Card 06 "Fade" — the shield bar keeps its full length and DIMS: the
 * alpha follows the shield, so a full shield is a solid white line on the
 * hull bar and a spent one is a faint mark at the floor. A contact has a
 * flat hull bar inside a white frame that dims the same way.
 */
const fade: BarStyle = {
  contactPlace: (f) => f.fade.contactPlace,
  cargoY: (f) => f.fade.cargoY,
  own(s, v, f) {
    const c = f.fade;
    const top = f.ownHullY + c.groundDy;
    ground(s, f, f.x, top, f.w, c.groundH, c.groundAlpha);
    s.rect(f.x, top, f.w, c.groundH, f.shieldColor,
      c.alphaFloor + span(c.alphaSpan, v.shieldPct));
    hullBar(s, v, f, f.ownHullY);
  },
  contact(s, v, f) {
    const c = f.fade;
    const top = f.ownHullY;
    flatBar(s, v, f, top);
    frame(s, f.x - c.framePad, top - c.framePad, f.w + 2 * c.framePad, c.frameH, c.frameW,
      f.shieldColor, c.frameFloor + span(c.frameSpan, v.shieldPct));
  },
};

/** One drawing per value of the `shield` field (themes.md §3.5). */
export const BAR_STYLES: Record<ShieldStyle, BarStyle> = {
  stack, cap, sheath, ticks, lanes, fade,
};
